import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { HexItem } from '@/hooks/useHexItems'
import type { ExpansionPhase } from '@/hooks/useHexExpansion'
import { HexTile } from './HexTile'
import { computeHoneycombLayout } from './layout'

interface Props {
  items: HexItem[]
  selectedId?: string | null
  expandedId: string | null
  phase: ExpansionPhase
  canDrill: boolean
  onToggle: (id: string) => void
  onDrill: (id: string) => void
  onClose: () => void
}

export function HexGrid({
  items,
  selectedId,
  expandedId,
  phase,
  canDrill,
  onToggle,
  onDrill,
  onClose,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = useState(0)

  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const measure = () => setContainerW(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const layout = useMemo(
    () => computeHoneycombLayout(items.map((i) => i.id), expandedId, containerW),
    [items, expandedId, containerW],
  )

  const byId = useMemo(
    () => new Map(layout.items.map((p) => [p.id, p])),
    [layout],
  )

  return (
    <div
      ref={wrapRef}
      onClick={(e) => {
        if (e.target === e.currentTarget && expandedId) onClose()
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: layout.contentHeight,
        boxSizing: 'border-box',
      }}
    >
      {items.map((item) => {
        const pos = byId.get(item.id)
        if (!pos) return null
        return (
          <HexTile
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            expanded={item.id === expandedId}
            phase={item.id === expandedId ? phase : 'push'}
            gridPhase={phase}
            canDrill={canDrill}
            x={pos.x}
            y={pos.y}
            width={pos.width}
            height={pos.height}
            onToggle={() => onToggle(item.id)}
            onDrill={() => onDrill(item.id)}
            onClose={onClose}
          />
        )
      })}
    </div>
  )
}
